/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Phone Case Design Page - Step-based Flow for Mobile
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ChevronUpIcon, ChevronDownIcon } from '../components/icons';
import StartScreen from '../components/StartScreen';
import Canvas from '../components/Canvas';
import WardrobePanel from '../components/WardrobeModal';
import OutfitStack from '../components/OutfitStack';
import { DesignLayer, CaseModel } from '../types';
import { defaultWardrobe } from '../wardrobe';
import Footer from '../components/Footer';
import { getFriendlyErrorMessage } from '../lib/utils';
import { modalApi } from '../services/modalApi';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { templateService } from '../services/templateService';
import { useSystemSettings } from '../hooks/useSystemSettings';
import CompletionPage from '../components/CompletionPage';
// import { BackgroundRemovalToggle } from '../components/BackgroundRemovalToggle'; // Hidden until feature is ready
import MobileNav from '../components/MobileNav';
import { RadialProgress } from '../components/RadialProgress';
import { LOADING_CONFIG } from '../lib/loadingConfig';
import PositionSelector, { Position } from '../components/PositionSelector';
import ProductFilter, { FilterState } from '../components/ProductFilter';
import { indexedDBService } from '../services/indexedDbService';

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);
    mediaQueryList.addEventListener('change', listener);
    return () => mediaQueryList.removeEventListener('change', listener);
  }, [query]);
  return matches;
};

type Step = 1 | 2 | 3 | 4 | 5;

// Print Style Types
type PrintStyle = 'FULL_WRAP' | 'CENTER_STICKER' | 'TILED' | 'VERTICAL_STRIP' | 'HORIZONTAL_STRIP' | 'DIAGONAL' | 'UPPER_HALF' | 'LOWER_HALF' | 'TOP_CORNER' | 'BOTTOM_CORNER';

interface Font {
  id: string;
  name: string;
}

interface PrintStyleOption {
  id: PrintStyle;
  label: string;
  icon: string;
}

// Font Options
const FONTS: Font[] = [
  { id: 'Inter', name: 'Hiện đại (Inter)' },
  { id: 'Playfair Display', name: 'Sang trọng (Serif)' },
  { id: 'Dancing Script', name: 'Nghệ thuật (Script)' },
  { id: 'Bebas Neue', name: 'Mạnh mẽ (Bold)' },
  { id: 'Courier Prime', name: 'Cổ điển (Mono)' },
];

// Print Style Options
const PRINT_STYLES: PrintStyleOption[] = [
  { id: 'FULL_WRAP', label: 'Toàn bộ', icon: '■' },
  { id: 'CENTER_STICKER', label: 'Chính giữa', icon: '▣' },
  { id: 'TILED', label: 'Lặp lại', icon: '▦' },
  { id: 'VERTICAL_STRIP', label: 'Dọc', icon: '▮' },
  { id: 'HORIZONTAL_STRIP', label: 'Ngang', icon: '▬' },
  { id: 'DIAGONAL', label: 'Chéo', icon: '◪' },
  { id: 'UPPER_HALF', label: 'Nửa trên', icon: '⬔' },
  { id: 'LOWER_HALF', label: 'Nửa dưới', icon: '⬕' },
  { id: 'TOP_CORNER', label: 'Góc trên', icon: '◹' },
  { id: 'BOTTOM_CORNER', label: 'Góc dưới', icon: '◺' },
];

const PhoneDesignPage: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();

  // Step management
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Log component mount
  useEffect(() => {
    console.log('📱 PhoneDesignPage mounted');
    console.log('   Current step:', currentStep);
  }, []);

  // Image data
  const [designImageUrl, setDesignImageUrl] = useState<string | null>(null);
  const [designImageId, setDesignImageId] = useState<string | null>(null);
  const [designImagePath, setDesignImagePath] = useState<string | null>(null);

  // Design state
  const [designHistory, setDesignHistory] = useState<DesignLayer[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<CaseModel[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CaseModel | null>(null);
  const [removeBackground, setRemoveBackground] = useState(false);
  const [position, setPosition] = useState<Position>('center');
  const [style, setStyle] = useState<string>('default');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [filters, setFilters] = useState<FilterState>({ model: null, variant: null, color: null });

  // New UI state
  const [isSheetCollapsed, setIsSheetCollapsed] = useState(false);
  const [userText, setUserText] = useState('');
  const [selectedFont, setSelectedFont] = useState('Inter');
  const [printStyle, setPrintStyle] = useState<PrintStyle>('CENTER_STICKER');

  // Completion state
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const isMobile = useMediaQuery('(max-width: 767px)');

  // Auto-expand sheet when changing steps
  useEffect(() => {
    if (currentStep === 2 || currentStep === 3) {
      setIsSheetCollapsed(false);
    }
  }, [currentStep]);

  // Load phone case templates from Firestore
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templates = await templateService.getTemplates('phone_case');
        setAvailableTemplates(templates);
      } catch (err) {
        console.error("Failed to load phone case templates", err);
        setAvailableTemplates([]);
      }
    };
    fetchTemplates();
  }, []);

  const displayImageUrl = useMemo(() => {
    if (designHistory.length > 0 && designHistory[currentIndex]) {
      return designHistory[currentIndex].resultImage;
    }
    return designImageUrl;
  }, [designHistory, currentIndex, designImageUrl]);

  const activeCaseIds = useMemo(() =>
    designHistory.map(layer => layer.caseModel?.id).filter(Boolean) as string[],
    [designHistory]
  );

  // Filter templates based on selected filters
  const filteredTemplates = useMemo(() => {
    return availableTemplates.filter((template) => {
      const name = template.name.toLowerCase();
      const category = template.category?.toLowerCase() || '';

      // Filter by model (15, 16, 17)
      if (filters.model) {
        if (!name.includes(filters.model) && !category.includes(filters.model)) {
          return false;
        }
      }

      // Filter by variant (Pro, Pro Max, base)
      if (filters.variant) {
        const variantLower = filters.variant.toLowerCase();
        if (variantLower === 'base') {
          // Base model shouldn't have "pro" in the name
          if (name.includes('pro') || category.includes('pro')) {
            return false;
          }
        } else {
          if (!name.includes(variantLower) && !category.includes(variantLower)) {
            return false;
          }
        }
      }

      // Filter by color
      if (filters.color) {
        const colorLower = filters.color.toLowerCase();
        if (!name.includes(colorLower) && !category.includes(colorLower)) {
          return false;
        }
      }

      return true;
    });
  }, [availableTemplates, filters]);

  // Step 1: Upload image
  const handleDesignUploaded = (data: { imageId: string; filepath: string; previewUrl: string }) => {
    console.log('✅ Image uploaded:', {
      imageId: data.imageId,
      filepath: data.filepath
    });
    setDesignImageUrl(data.previewUrl);
    setDesignImageId(data.imageId);
    setDesignImagePath(data.filepath);
    setDesignHistory([]);
    setCurrentIndex(0);
    setCurrentStep(2); // Move to step 2
  };

  // Step 2: Background removed handler (if needed)
  const handleBackgroundRemoved = (processedPath: string, previewUrl: string) => {
    setDesignImagePath(processedPath);
    setDesignImageUrl(previewUrl);
  };

  const handleStartOver = () => {
    console.log('🔄 Starting over - resetting all state');
    setDesignImageUrl(null);
    setDesignImageId(null);
    setDesignImagePath(null);
    setDesignHistory([]);
    setCurrentIndex(0);
    setError(null);
    setRemoveBackground(false);
    setCurrentStep(1);
  };

  // Step 3: Select case template
  const handleCaseSelect = useCallback(async (caseFile: File, caseInfo: CaseModel) => {
    console.log('📱 Case selected:', {
      caseName: caseInfo.name,
      caseId: caseInfo.id,
      designImagePath
    });

    if (!designImagePath && !designImageId) {
      alert("Vui lòng tải ảnh lên trước.");
      return;
    }

    if (isLoading) return;

    setError(null);
    setIsLoading(true);
    setSelectedTemplate(caseInfo); // Track selected template
    setLoadingMessage(`Đang tạo mẫu trên ${caseInfo.name}...`);

    // Move to step 4 IMMEDIATELY to show loading state
    setCurrentStep(4);

    try {
      // Lazy upload: Check IndexedDB and upload if needed
      let actualImageId = designImageId;
      let actualFilepath = designImagePath;

      if (designImagePath === '' || !designImagePath) {
        console.log('[PhoneDesignPage] 🔍 Checking IndexedDB for design image...');
        const cached = await indexedDBService.getDesignImage(designImageId);

        if (!cached) {
          throw new Error('Design image not found in cache');
        }

        if (!cached.uploaded) {
          // Upload to Modal now
          console.log('[PhoneDesignPage] 📤 Uploading design image to Modal (lazy upload)...');
          setLoadingMessage('Đang tải ảnh lên server...');

          const uploadResponse = await modalApi.uploadImage(cached.base64);

          if (uploadResponse.success) {
            actualImageId = uploadResponse.image_id;
            actualFilepath = uploadResponse.filepath;

            // Mark as uploaded in IndexedDB
            await indexedDBService.markAsUploaded(
              designImageId!,
              uploadResponse.image_id,
              uploadResponse.filepath
            );

            console.log('[PhoneDesignPage] ✅ Image uploaded successfully');
          } else {
            throw new Error('Upload failed');
          }
        } else {
          // Already uploaded, use cached metadata
          actualImageId = cached.uploadedImageId!;
          actualFilepath = cached.uploadedFilepath!;
          console.log('[PhoneDesignPage] ✅ Using cached upload metadata');
        }

        setLoadingMessage(`Đang tạo mẫu trên ${caseInfo.name}...`);
      }

      console.log('🚀 Calling generateMockup API...');
      const response = await modalApi.generateMockup(
        actualFilepath,
        caseInfo.url,
        removeBackground,
        {
          product_type: 'phone_case',
          image_id: actualImageId || undefined,
          template_id: caseInfo.id || undefined,
          position: position,
          style: style,
          custom_prompt: customPrompt,
          // NEW PARAMETERS:
          text_overlay: userText,
          font_family: selectedFont,
          print_style: printStyle
        }
      );

      console.log('✅ Mockup generated successfully:', {
        mockupId: response.mockup_id,
        mockupPath: response.mockup_path
      });

      const newLayer: DesignLayer = {
        caseModel: caseInfo,
        resultImage: response.image,
        mockupId: response.mockup_id,
        mockupPath: response.mockup_path,
        mockupImageData: response.image, // Store base64 for reliable access later
        designImageId: actualImageId!,
        designImagePath: actualFilepath
      };
      setDesignHistory(prev => {
        const newHistory = [...prev, newLayer];
        setCurrentIndex(newHistory.length - 1);
        return newHistory;
      });
      // Already at step 4
    } catch (err) {
      console.error("❌ Error in handleCaseSelect:", err);
      setError(getFriendlyErrorMessage(err, 'Tạo mẫu ốp lưng thất bại'));
      setCurrentStep(3); // Go back to step 3 on error
    } finally {
      setIsLoading(false);
      setSelectedTemplate(null); // Clear selection after processing
    }
  }, [designImagePath, designImageId, isLoading, removeBackground, position, style, customPrompt, userText, selectedFont, printStyle]);

  const handleRemoveLayer = () => {
    if (designHistory.length > 0) {
      setDesignHistory(prev => {
        const newHistory = prev.slice(0, -1);
        setCurrentIndex(Math.max(0, newHistory.length - 1));
        return newHistory;
      });
    }
  };

  const handleSubmit = async () => {
    if (!designHistory[currentIndex]) return;

    setIsLoading(true);
    setError(null);
    setLoadingMessage('Đang lưu lại thiết kế của bạn...');

    try {
      const layer = designHistory[currentIndex];

      // ✅ Validate required mockup fields
      if (!layer.mockupId || !layer.mockupPath) {
        throw new Error('Thiết kế chưa hoàn tất. Vui lòng chọn lại mẫu sản phẩm.');
      }

      // Get or generate visitor ID
      let visitorId = localStorage.getItem('veritas_visitor_id');
      if (!visitorId) {
        visitorId = crypto.randomUUID();
        localStorage.setItem('veritas_visitor_id', visitorId);
      }

      // Generate a session ID if one doesn't exist
      let sessionId = localStorage.getItem('veritas_session_id');
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        localStorage.setItem('veritas_session_id', sessionId);
      }

      const result = await modalApi.createSubmission({
        imageId: layer.designImageId!,
        originalImagePath: layer.designImagePath!,
        mockupId: layer.mockupId,
        mockupImagePath: layer.mockupPath,
        productType: 'phone_case',
        visitorId,
        sessionId,
        designOptions: {
          removeBackground,
          textOverlay: userText,
          printStyle
        },
        templateId: layer.caseModel?.id,
        templateName: layer.caseModel?.name
      });

      if (result.success) {
        setSubmissionId(result.submissionId);
        setCurrentStep(5);
      }
    } catch (err) {
      console.error("❌ Submission failed:", err);
      setError(getFriendlyErrorMessage(err, 'Gửi thiết kế thất bại. Vui lòng thử lại.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      console.log(`⬅️ Back button: ${currentStep} → ${currentStep - 1}`);
      setCurrentStep((currentStep - 1) as Step);
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      console.log(`➡️ Next button: ${currentStep} → ${currentStep + 1}`);
      setCurrentStep((currentStep + 1) as Step);
    }
  };

  // Render Step 1: Upload
  const renderStep1 = () => (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <StartScreen
        onDesignReady={handleDesignUploaded}
        mode="phone"
        title="Thiết Kế Ốp Lưng Điện Thoại"
        description="Biến những bức ảnh yêu thích thành phụ kiện thời trang. AI sẽ tạo mockup in UV chất lượng cao lên ốp lưng."
      />
    </div>
  );

  // Render Step 2: Options
  const renderStep2 = () => (
    <div className="flex flex-col md:flex-row min-h-full w-full overflow-auto relative bg-slate-50">
      {/* MOBILE HEADER: Preview Banner (Sticky at top on mobile) */}
      <div className="md:hidden sticky top-0 z-30 w-full h-40 bg-slate-100 border-b border-slate-200 flex items-center justify-center p-4">
        {designImageUrl && (
          <img src={designImageUrl} alt="Preview" className="h-full w-auto object-contain rounded-lg shadow-sm" />
        )}
      </div>

      {/* LEFT COLUMN: Canvas Preview (Desktop only) */}
      <div className="hidden md:flex md:w-1/2 h-full items-center justify-center bg-slate-50 p-6">
        {designImageUrl && (
          <div className="relative w-full max-w-md">
            <img
              src={designImageUrl}
              alt="Design Preview"
              className="w-full h-auto rounded-xl shadow-2xl max-h-[500px] object-contain border border-white/20"
            />
          </div>
        )}
      </div>

      {/* RIGHT COLUMN / MOBILE CONTENT: Scrollable Options */}
      <div className="flex-1 md:w-1/2 min-h-full flex flex-col bg-white overflow-auto md:border-l border-slate-200">
        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 pb-24 mobile-scroll-smooth">
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-100 flex items-center gap-2">
              <span className="text-lg">⚠️</span> {error}
            </div>
          )}

          {/* Intro Box */}
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="p-2 bg-indigo-100 rounded-lg text-lg">📱</span>
              <p className="text-base font-bold text-slate-900 uppercase tracking-tight">Ốp Lưng In UV</p>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              In UV chất lượng cao trên ốp lưng silicon. Màu sắc sống động, không phai, chống trầy xước.
            </p>
          </div>

          {/* Background Toggle */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between shadow-sm hover:border-indigo-200 transition-colors">
            <div className="flex-1 pr-4">
              <p className="text-sm font-bold text-slate-900 mb-0.5">Tách nền AI</p>
              <p className="text-xs text-slate-500">Chỉ in chủ thể, tự động xóa phông</p>
            </div>
            <button
              onClick={() => setRemoveBackground(!removeBackground)}
              className={`w-12 h-6 rounded-full relative transition-all duration-300 flex-shrink-0 ${removeBackground ? 'bg-indigo-600 shadow-inner' : 'bg-slate-200'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300 ${removeBackground ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

          {/* Text Input Section */}
          <div className="bg-slate-900 p-5 rounded-2xl shadow-xl border border-white/10 space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-[11px] font-black text-indigo-400 uppercase tracking-[2px]">In chữ cá nhân</p>
              <span className="text-[9px] text-indigo-300/50 font-bold bg-white/5 px-2 py-1 rounded-full uppercase">Tùy chọn</span>
            </div>
            <p className="text-[10px] text-slate-400 leading-snug">Ghi tên, nickname hoặc câu bạn muốn in lên ốp.</p>
            <div className="space-y-3">
              <input
                type="text"
                value={userText}
                onChange={(e) => setUserText(e.target.value)}
                placeholder="Ví dụ: Anh Tuấn, Happy Day..."
                className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-white placeholder:text-slate-600 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all"
              />
              {userText.trim() && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                  <p className="text-[10px] text-slate-500 uppercase font-black tracking-tight">Kiểu chữ</p>
                  <select
                    value={selectedFont}
                    onChange={(e) => setSelectedFont(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white outline-none cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    {FONTS.map(f => <option key={f.id} value={f.id} className="text-slate-900">{f.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* AI Prompt */}
          <div className="space-y-3">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Ghi chú thiết kế</p>
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Mô tả cụ thể yêu cầu của bạn..."
              className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-900 shadow-sm focus:ring-2 focus:ring-slate-400/20 transition-all active:scale-[0.99] h-24 resize-none"
            />
          </div>

          {/* Style Selector */}
          <div className="space-y-4">
            <p className="text-[11px] font-black text-slate-900 uppercase tracking-[2px] px-1">Bố cục in ảnh</p>
            <div className="flex overflow-x-auto gap-4 pb-4 px-1 snap-x scrollbar-hide -mx-4 md:mx-0 px-4 md:px-0">
              {PRINT_STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setPrintStyle(s.id)}
                  className={`flex-shrink-0 flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border transition-all duration-300 w-24 h-24 snap-start ${printStyle === s.id
                    ? 'bg-slate-900 text-white border-slate-900 shadow-2xl scale-110 z-10'
                    : 'bg-white text-slate-500 border-slate-100 hover:border-indigo-200 hover:text-slate-900'
                    }`}
                >
                  <span className="text-2xl">{s.icon}</span>
                  <span className="text-[10px] font-black uppercase text-center leading-tight">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* STICKY FOOTER */}
        <div className="sticky bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-xl border-t border-slate-100 z-40 md:static md:bg-transparent md:border-none md:p-8">
          <button
            onClick={handleNext}
            className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl hover:bg-slate-800 transition-all shadow-xl active:scale-95 text-base uppercase tracking-widest"
          >
            Tiếp tục
          </button>
        </div>
      </div>
    </div>
  );

  // Render Step 3: Template Selection
  const renderStep3 = () => (
    <div className="flex flex-col md:flex-row min-h-full w-full overflow-auto relative bg-slate-50">
      {/* MOBILE HEADER: Preview Banner (Sticky on mobile) */}
      <div className="md:hidden sticky top-0 z-30 w-full h-40 bg-slate-100 border-b border-slate-200 flex items-center justify-center p-4">
        {designImageUrl && (
          <img src={designImageUrl} alt="Logo" className="h-full w-auto object-contain rounded-lg shadow-sm" />
        )}
      </div>

      {/* LEFT COLUMN: Design Preview (Desktop only) */}
      <div className="hidden md:flex md:w-1/2 h-full items-center justify-center bg-slate-50 p-6">
        {designImageUrl && (
          <div className="relative w-full max-w-md">
            <img
              src={designImageUrl}
              alt="Design Preview"
              className="w-full h-auto rounded-xl shadow-2xl max-h-[500px] object-contain border border-white/20"
            />
          </div>
        )}
      </div>

      {/* RIGHT COLUMN / MOBILE CONTENT: Scrollable Templates */}
      <div className="flex-1 md:w-1/2 min-h-full flex flex-col bg-white overflow-auto md:border-l border-slate-200">
        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 pb-24 mobile-scroll-smooth">
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-100 mb-4">
              {error}
            </div>
          )}

          {/* Product Filter */}
          <div className="mb-6">
            <ProductFilter filters={filters} onFilterChange={setFilters} />
          </div>

          <div className="content-area">
            {availableTemplates.length === 0 ? (
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-8 text-center">
                <p className="text-amber-800 font-medium">⚠️ Chưa có mẫu ốp lưng nào trong hệ thống.</p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8 text-center">
                <p className="text-slate-600 font-medium">🔍 Không tìm thấy sản phẩm. Thử đổi bộ lọc nhé!</p>
              </div>
            ) : (
              <WardrobePanel
                onGarmentSelect={handleCaseSelect}
                activeGarmentIds={activeCaseIds}
                isLoading={isLoading}
                wardrobe={filteredTemplates}
                loadingMessages={LOADING_CONFIG.mockup.phone}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render Step 4: Result
  const renderStep4 = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex items-center justify-center p-4 bg-slate-50">
        <Canvas
          displayImageUrl={displayImageUrl}
          onStartOver={handleStartOver}
          isLoading={isLoading}
          loadingMessage={loadingMessage}
          loadingMessages={LOADING_CONFIG.mockup.phone}
        />
      </div>

      <div className="p-4 bg-white border-t border-slate-200 space-y-4">
        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={isLoading || designHistory.length === 0}
            className="w-full bg-slate-900 text-white font-bold py-4 rounded-lg hover:bg-slate-800 transition-all shadow-lg active:scale-98 min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? 'Đang gửi...' : 'Xác nhận thiết kế'}
          </button>
        </div>

        {designHistory.length > 0 && (
          <OutfitStack history={designHistory} onRemoveLast={handleRemoveLayer} />
        )}
      </div>
    </div>
  );

  // Render Step 5: Completion
  const renderStep5 = () => {
    if (!submissionId || !designHistory[currentIndex]) return null;

    return (
      <CompletionPage
        designInfo={{
          orderId: submissionId,
          productType: 'phone_case',
          productName: designHistory[currentIndex].caseModel?.name,
          mockupImageUrl: displayImageUrl || '',
          designOptions: {
            removeBackground,
            textOverlay: userText,
            printStyle
          }
        }}
        shopUrl="https://custom.airxcoffee.com/cart"
      />
    );
  };

  return (
    <div className="font-sans min-h-screen bg-white flex flex-col">
      {/* Mobile Navigation */}
      <MobileNav showDesignButton={false} />

      {/* Realtime Announcement Banner */}
      {settings?.announcement_banner && (
        <div className="bg-slate-700 text-white text-center py-2 px-4 text-sm font-medium relative z-40">
          📢 {settings.announcement_banner}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col min-h-[calc(100vh-56px)] bg-white flex-1">
        {/* Progress Indicator - Hide on step 5 */}
        {currentStep < 5 && (
          <div className="w-full bg-white border-b border-slate-100 py-3 flex justify-center sticky top-0 z-30">
            <RadialProgress
              currentStep={currentStep}
              totalSteps={4}
              color="#334155" // slate-700
              size={isMobile ? 60 : 80}
            />
          </div>
        )}

        {/* Back Button (if not on first step) */}
        {currentStep > 1 && currentStep < 5 && (
          <div className="absolute top-[84px] left-4 z-30 md:top-20">
            <button
              onClick={handleBack}
              className="flex items-center justify-center gap-2 bg-white/95 backdrop-blur-sm border border-slate-200 text-slate-700 font-medium py-2.5 px-4 rounded-lg transition-all duration-200 hover:bg-white hover:shadow-md active:scale-95 min-h-[44px]"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Quay lại</span>
            </button>
          </div>
        )}

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                {renderStep1()}
              </motion.div>
            )}
            {currentStep === 2 && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                {renderStep2()}
              </motion.div>
            )}
            {currentStep === 3 && (
              <motion.div
                key="step-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                {renderStep3()}
              </motion.div>
            )}
            {currentStep === 4 && (
              <motion.div
                key="step-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                {renderStep4()}
              </motion.div>
            )}
            {currentStep === 5 && (
              <motion.div
                key="step-5"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="h-full overflow-y-auto"
              >
                {renderStep5()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {currentStep < 5 && <Footer isOnDressingScreen={currentStep > 1} />}
    </div>
  );
};

export default PhoneDesignPage;